import React from 'react';
import { Typography, Stack, Button, Chip } from '@mui/material';

type ParamVal = string | number | boolean | { [k: string]: any };

interface TemplateButton {
  label?: string;
  text?: string;
  value?: string;
  type?: string;
}

interface TemplateBody {
  text?: string;
  parameters?: ParamVal[] | { [k: string]: ParamVal[] | ParamVal };
}

interface TemplateHeaderFooter {
  text?: string;
}

interface TemplateContent {
  template_name?: string;
  header?: TemplateHeaderFooter | string;
  body?: TemplateBody | string;
  footer?: TemplateHeaderFooter | string;
  buttons?: TemplateButton[];
}

interface MessageTemplateContentProps {
  content?: TemplateContent;
  onButtonClick?: (button: TemplateButton) => void;
}

// helpers
const asText = (block?: string | { text?: string }) =>
  typeof block === 'string' ? block : block?.text || '';

const stringifyParam = (v: ParamVal): string => {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  if ((v as any).text) return String((v as any).text);
  if ((v as any).value) return String((v as any).value);
  return JSON.stringify(v);
};

const flattenParams = (params: TemplateBody['parameters']): string[] => {
  if (!params) return [];
  if (Array.isArray(params)) return params.map(stringifyParam);
  const out: string[] = [];
  Object.values(params).forEach((v) => {
    if (Array.isArray(v)) v.forEach((item) => out.push(stringifyParam(item)));
    else out.push(stringifyParam(v));
  });
  return out;
};

const fillPlaceholders = (text: string, values: string[]) =>
  text?.replace(/{{\s*(\d+)\s*}}/g, (_m, idxStr) => {
    const idx = parseInt(idxStr, 10) - 1;
    const replacement = values[idx];
    return replacement?.length ? replacement : `{{${idxStr}}}`;
  }) || '';

const MessageTemplate: React.FC<MessageTemplateContentProps> = ({
  content = {},
  onButtonClick,
}) => {
  const headerText = asText(content.header);
  const footerText = asText(content.footer);

  let bodyText = '';
  let paramValues: string[] = [];
  if (typeof content.body === 'string') {
    bodyText = content.body;
  } else if (content.body) {
    bodyText = content.body.text || '';
    paramValues = flattenParams(content.body.parameters);
  }
  const resolvedBody = fillPlaceholders(bodyText, paramValues);

  const buttons = Array.isArray(content.buttons) ? content.buttons : [];

  return (
    <Stack spacing={1}>
      {/* Template tags */}
      <Stack direction="row" spacing={1}>
        <Chip size="small" label="Template" color="primary" variant="outlined" />
        {content.template_name && (
          <Chip size="small" variant="outlined" label={content.template_name} />
        )}
      </Stack>

      {/* Header */}
      {headerText && (
        <Typography variant="subtitle1" fontWeight="bold" sx={{ whiteSpace: 'pre-wrap' }}>
          {headerText}
        </Typography>
      )}

      {/* Body */}
      {resolvedBody && (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
          {resolvedBody}
        </Typography>
      )}

      {/* Buttons */}
      {buttons.length > 0 && (
        <Stack spacing={1}>
          {buttons.map((btn, i) => (
            <Button
              key={i}
              variant={btn.type?.toUpperCase().includes('URL') ? 'outlined' : 'contained'}
              size="small"
              onClick={() =>
                btn.type?.toUpperCase().includes('URL') && btn.value
                  ? window.open(btn.value, '_blank', 'noopener,noreferrer')
                  : onButtonClick?.(btn)
              }
              sx={{ textTransform: 'none', borderRadius: 20, justifyContent: 'flex-start' }}
            >
              {btn.label || btn.text || 'Open'}
            </Button>
          ))}
        </Stack>
      )}

      {/* Footer */}
      {footerText && (
        <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'pre-wrap' }}>
          {footerText}
        </Typography>
      )}
    </Stack>
  );
};

export default MessageTemplate;
